import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  X,
  Loader2,
  Navigation,
  FileText,
  User,
  Phone,
  Check,
  Briefcase,
  ChevronDown,
} from "lucide-react";
import { getDatabase, ref, get, set, update } from "firebase/database";
import bcrypt from "bcryptjs";
import app from "../../firebase";

const db = getDatabase(app);

// Add this to your component file (before the component definition)
const styles = `
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

// Data for state and city dropdowns
const indianStatesAndCities = {
  "Andhra Pradesh": [
    "Alluri Sitharama Raju",
      "Anakapalli",
      "Annamayya",
      "Anantapuramu",
      "Bapatla",
      "Chittoor",
      "Dr. B.R. Ambedkar Konaseema",
      "East Godavari",
      "Eluru",
      "Guntur",
      "Kakinada",
      "Krishna",
      "Kurnool",
      "Palnadu",
      "NTR",
      "Parvathipuram Manyam",
      "Nandyal",
      "Prakasam",
      "Srikakulam",
      "Sri Potti Sri Ramulu Nellore",
      "Sri Sathya Sai",
      "Tirupati",
      "Visakhapatnam",
      "Vizianagaram",
      "West Godavari",
      "YSR Kadapa"
  ],
  "Telangana": [
     "Adilabad",
      "Bhadradri Kothagudem",
      "Hyderabad",
      "Jagtial",
      "Jangaon",
      "Jayashankar Bhupalpally",
      "Jogulamba Gadwal",
      "Kamareddy",
      "Karimnagar",
      "Khammam",
      "Kumuram Bheem Asifabad",
      "Mahabubabad",
      "Mahabubnagar",
      "Mancherial",
      "Medak",
      "Medchal Malkajgiri",
      "Mulugu",
      "Nagarkurnool",
      "Nalgonda",
      "Narayanpet",
      "Nirmal",
      "Nizamabad",
      "Peddapalli",
      "Rajanna Sircilla",
      "Ranga Reddy",
      "Sangareddy",
      "Siddipet",
      "Suryapet",
      "Vikarabad",
      "Wanaparthy",
      "Warangal",
      "Yadadri Bhuvanagiri"
  ]
};

// Toast component
const Toast = ({ message, type, onClose }) => {
  const bgColor = type === 'error' ? 'bg-red-500' : 'bg-green-500';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`fixed bottom-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center justify-between min-w-[250px] z-50`}
    >
      <div className="flex items-center">
        {type === 'error' ? (
          <X className="w-5 h-5 mr-2" />
        ) : (
          <Check className="w-5 h-5 mr-2" />
        )}
        <span>{message}</span>
      </div>
      <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

const ContractorLogin = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    licenseNumber: "",
    contractorType: "",
    specialties: [],
    experience: "",
    teamSize: "",
    insurance: false,
    safetyCertified: false,
    address: "",
    city: "",
    state: "",
    pincode: ""
  });
  const [cities, setCities] = useState([]);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [toasts, setToasts] = useState([]);
  const [background, setBackground] = useState({
    from: "from-orange-400",
    via: "via-amber-500",
    to: "to-yellow-500"
  });

  // Add a toast message
  const addToast = (message, type = 'error') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  // Remove a toast
  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Update background when form changes
  useEffect(() => {
    if (isLogin) {
      setBackground({
        from: "from-orange-400",
        via: "via-amber-500",
        to: "to-yellow-500"
      });
    } else {
      setBackground({
        from: "from-orange-300",
        via: "via-amber-400",
        to: "to-yellow-400"
      });
    }
  }, [isLogin]);

  // Check for saved user data on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem('contractorData');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setFormData(prev => ({
        ...prev,
        email: userData.email || "",
        phone: userData.phone || "",
      }));
    }
  }, []);

  // Get user's current location
  const getCurrentLocation = () => {
    setLocationLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            if (data.error) {
              addToast("Could not determine address from coordinates");
            } else {
              const address = data.address;
              const detectedState = address.state || "";

              if (detectedState && indianStatesAndCities[detectedState]) {
                setCities(indianStatesAndCities[detectedState]);
              } else {
                setCities([]);
              }

              setLocation({
                address: data.display_name,
                city: address.city || address.town || address.village,
                state: detectedState,
                pincode: address.postcode,
                coordinates: { latitude, longitude }
              });
              
              setFormData(prev => ({
                ...prev,
                address: data.display_name,
                city: address.city || address.town || address.village || "",
                state: detectedState,
                pincode: address.postcode || "",
              }));
            }
          } catch (error) {
            addToast("Failed to fetch location details");
          } finally {
            setLocationLoading(false);
          }
        },
        (error) => {
          setLocationLoading(false);
          addToast("Location access denied. Please enable location services.");
        }
      );
    } else {
      setLocationLoading(false);
      addToast("Geolocation is not supported by your browser");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };

    if (name === "state") {
      setCities(indianStatesAndCities[value] || []);
      newFormData.city = ""; // Reset city on state change
    }

    setFormData(newFormData);
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
    const licenseRegex = /^[A-Za-z0-9]{8,15}$/;

    if (!isLogin) {
      if (!formData.firstName.trim()) errors.firstName = "First name is required";
      if (!formData.lastName.trim()) errors.lastName = "Last name is required";
      if (!formData.companyName.trim()) errors.companyName = "Company name is required";
      if (!formData.licenseNumber) {
        errors.licenseNumber = "License number is required";
      } else if (!licenseRegex.test(formData.licenseNumber)) {
        errors.licenseNumber = "Invalid license number format";
      }
      if (!formData.contractorType) errors.contractorType = "Contractor type is required";
      if (formData.specialties.length === 0) errors.specialties = "At least one specialty is required";
      if (!formData.experience) errors.experience = "Experience is required";
      if (!formData.teamSize) errors.teamSize = "Team size is required";
    }
    
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email format";
    }
    
    if (!formData.phone) {
      errors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone = "Invalid phone number (10 digits required)";
    }
    
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (!isLogin && !passwordRegex.test(formData.password)) {
      errors.password = "Password must be at least 8 characters with letters and numbers";
    }

    if (!isLogin && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!isLogin) {
      if (!formData.state) errors.state = "State is required";
      if (!formData.city) errors.city = "District is required";
      if (!formData.pincode) errors.pincode = "Pincode is required";
    }
    
    const firstError = Object.values(errors)[0];
    if (firstError) {
      addToast(firstError);
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSpecialtyChange = (specialty) => {
    setFormData(prev => {
      if (prev.specialties.includes(specialty)) {
        return {
          ...prev,
          specialties: prev.specialties.filter(s => s !== specialty)
        };
      } else {
        return {
          ...prev,
          specialties: [...prev.specialties, specialty]
        };
      }
    });
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Check if email exists
        const emailRef = ref(db, `ContractorEmails/${formData.email.replace(/\./g, ',')}`);
        const emailSnapshot = await get(emailRef);

        if (emailSnapshot.exists()) {
          addToast("Email already registered. Please login.");
          setIsSubmitting(false);
          return;
        }

        // Generate contractor ID in the format: state/city/licenseNumber
        const contractorId = `${formData.state}/${formData.city}/${formData.licenseNumber}`;
        const contractorRef = ref(db, `Contractors/${contractorId}`);

        // Check if contractor already exists
        const contractorSnapshot = await get(contractorRef);
        if (contractorSnapshot.exists()) {
          addToast("A contractor with this license number already exists in this location.");
          setIsSubmitting(false);
          return;
        }

        // Hash password
        const salt = await bcrypt.genSalt(10);
        const hashedPassword = await bcrypt.hash(formData.password, salt);

        // Create contractor data with ID
        const contractorData = {
          id: contractorId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: hashedPassword,
          companyName: formData.companyName,
          licenseNumber: formData.licenseNumber,
          contractorType: formData.contractorType,
          specialties: formData.specialties,
          experience: formData.experience,
          teamSize: formData.teamSize,
          insurance: formData.insurance,
          safetyCertified: formData.safetyCertified,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          coordinates: location?.coordinates || null,
          createdAt: Date.now(),
          lastLogin: Date.now(),
          status: "active",
          verificationStatus: "unverified",
          rating: 0,
          projectsCompleted: 0
        };

        // Save contractor data to database
        await set(contractorRef, contractorData);

        // Create email reference
        await set(emailRef, {
          contractorId: contractorId
        });

        // Prepare data for localStorage
        const userDataToStore = {
          ...contractorData,
          fullName: `${formData.firstName} ${formData.lastName}`,
          fullAddress: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`,
          password: undefined
        };

        // Save to localStorage
        localStorage.setItem('contractorData', JSON.stringify(userDataToStore));
        localStorage.setItem('contractorId', contractorId);
        localStorage.setItem('contractorEmail', formData.email);
        localStorage.setItem('contractorLocation', JSON.stringify({
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          coordinates: location?.coordinates || null
        }));

        addToast("Registration successful! Welcome to EZZO Contractors.", 'success');
        handleLoginSuccess();
        navigate('/contractor/dashboard');
      } catch (error) {
        console.error("Registration failed:", {
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
        addToast("Registration failed. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleLoginSuccess = () => {
    if (onSuccess) {
      window.location.reload();
      onSuccess();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginEmail || !loginPassword) {
      addToast("Please enter both email and password");
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if email exists in ContractorEmails
      const emailRef = ref(db, `ContractorEmails/${loginEmail.replace(/\./g, ',')}`);
      const emailSnapshot = await get(emailRef);

      if (!emailSnapshot.exists()) {
        addToast("Account not found. Please register first.");
        setIsSubmitting(false);
        return;
      }

      // Get contractor ID from email reference
      const { contractorId } = emailSnapshot.val();
      const contractorRef = ref(db, `Contractors/${contractorId}`);
      const contractorSnapshot = await get(contractorRef);

      if (!contractorSnapshot.exists()) {
        addToast("Account not found. Please register first.");
        setIsSubmitting(false);
        return;
      }

      const contractorData = contractorSnapshot.val();

      if (contractorData.status === "suspended") {
        addToast("Account suspended. Please contact support.");
        setIsSubmitting(false);
        return;
      }

      // Verify password
      const isMatch = await bcrypt.compare(loginPassword, contractorData.password);
      if (!isMatch) {
        addToast("Invalid password. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // Update last login
      await update(contractorRef, { lastLogin: Date.now() });

      // Prepare data for localStorage
      const userDataToStore = {
        ...contractorData,
        fullName: `${contractorData.firstName} ${contractorData.lastName}`,
        fullAddress: `${contractorData.address}, ${contractorData.city}, ${contractorData.state} - ${contractorData.pincode}`,
        password: undefined
      };

      // Save auth state
      localStorage.setItem('contractorData', JSON.stringify(userDataToStore));
      localStorage.setItem('contractorId', contractorId);
      localStorage.setItem('contractorEmail', loginEmail);
      localStorage.setItem('contractorLocation', JSON.stringify({
        address: contractorData.address,
        city: contractorData.city,
        state: contractorData.state,
        pincode: contractorData.pincode,
        coordinates: contractorData.coordinates || null
      }));

      addToast("Login successful! Welcome back.", 'success');
      handleLoginSuccess();
      navigate('/contractor/dashboard');
    } catch (error) {
      console.error("Login error:", {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      addToast("Login failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      addToast("Please enter your email address");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Check if email exists
      const emailRef = ref(db, `ContractorEmails/${forgotPasswordEmail.replace(/\./g, ',')}`);
      const emailSnapshot = await get(emailRef);

      if (!emailSnapshot.exists()) {
        addToast("Email not found. Please check your email or register.");
        setIsSubmitting(false);
        return;
      }

      addToast("Password reset instructions have been sent to your email (simulated for demo)", 'success');
      
      setForgotPasswordEmail('');
      setTimeout(() => {
        setShowForgotPassword(false);
      }, 3000);
    } catch (error) {
      console.error("Forgot password error:", error);
      addToast("Failed to process your request. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <style>{styles}</style>
    <div className={`bg-gradient-to-br ${background.from} ${background.via} ${background.to} flex items-center justify-center p-4 min-h-screen transition-colors duration-500`}>
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-orange-600 to-amber-600 p-6 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/concrete-wall.png')] opacity-10"></div>
          <Link to="/" className="text-center group relative">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="inline-flex flex-col items-center justify-center leading-none cursor-pointer"
            >
              <div className="flex items-center space-x-2">
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-4xl font-extrabold text-white drop-shadow-lg"
                >
                  EZZO
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-white font-bold text-lg self-end"
                >
                  Contractors
                </motion.span>
              </div>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.9 }}
                transition={{ delay: 0.5 }}
                className="text-xs text-amber-100 italic tracking-wide"
              >
                Build. Renovate. Transform.
              </motion.span>
            </motion.div>
          </Link>
        </motion.div>

        <div className="p-6 md:p-8">
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => {
                setIsLogin(true);
                setShowForgotPassword(false);
              }}
              className={`flex-1 py-2 font-medium text-sm ${isLogin && !showForgotPassword ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => {
                setIsLogin(false);
                setShowForgotPassword(false);
              }}
              className={`flex-1 py-2 font-medium text-sm ${!isLogin ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Register
            </button>
          </div>

          <AnimatePresence mode="wait">
            {showForgotPassword ? (
              <motion.div
                key="forgot-password"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                <p className="text-sm text-gray-600">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
                
                <form onSubmit={handleForgotPassword}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Reset Link"
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-all duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : isLogin ? (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="w-full pl-10 pr-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>

                  <div className="text-center text-sm text-gray-600 mt-4">
                    New to EZZO Contractors?{" "}
                    <button
                      type="button"
                      onClick={() => setIsLogin(false)}
                      className="text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Register your business
                    </button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="register"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <form onSubmit={handleRegistration} className="space-y-4 overflow-y-auto max-h-[60vh] p-2 no-scrollbar">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="John"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Doe"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Construction & Co."
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                        className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="CON12345678"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="9876543210"
                          required
                          maxLength="10"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="••••••••"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-700">Business Details</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contractor Type</label>
                        <select
                          name="contractorType"
                          value={formData.contractorType}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="General Contractor">General Contractor</option>
                          <option value="Electrical">Electrical</option>
                          <option value="Plumbing">Plumbing</option>
                          <option value="HVAC">HVAC</option>
                          <option value="Roofing">Roofing</option>
                          <option value="Carpentry">Carpentry</option>
                          <option value="Masonry">Masonry</option>
                          <option value="Landscaping">Landscaping</option>
                          <option value="Painting">Painting</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {['Residential', 'Commercial', 'Industrial', 'Renovation', 'New Construction', 'Remodeling', 'Custom Homes', 'Additions'].map(specialty => (
                            <div key={specialty} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`specialty-${specialty}`}
                                checked={formData.specialties.includes(specialty)}
                                onChange={() => handleSpecialtyChange(specialty)}
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`specialty-${specialty}`} className="ml-2 text-sm text-gray-700">
                                {specialty}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Experience (Years)</label>
                          <select
                            name="experience"
                            value={formData.experience}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required
                          >
                            <option value="">Select</option>
                            <option value="0-2">0-2 years</option>
                            <option value="3-5">3-5 years</option>
                            <option value="6-10">6-10 years</option>
                            <option value="10+">10+ years</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Team Size</label>
                          <select
                            name="teamSize"
                            value={formData.teamSize}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required
                          >
                            <option value="">Select</option>
                            <option value="1-5">1-5</option>
                            <option value="6-10">6-10</option>
                            <option value="11-20">11-20</option>
                            <option value="20+">20+</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="insurance"
                            checked={formData.insurance}
                            onChange={(e) => setFormData({...formData, insurance: e.target.checked})}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <label htmlFor="insurance" className="ml-2 text-sm text-gray-700">
                            Insured
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="safetyCertified"
                            checked={formData.safetyCertified}
                            onChange={(e) => setFormData({...formData, safetyCertified: e.target.checked})}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <label htmlFor="safetyCertified" className="ml-2 text-sm text-gray-700">
                            Safety Certified
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-700">Business Address</h3>
                      <button
                        type="button"
                        onClick={getCurrentLocation}
                        disabled={locationLoading}
                        className="flex items-center text-sm text-orange-600 hover:text-orange-700"
                      >
                        {locationLoading ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Detecting...
                          </>
                        ) : (
                          <>
                            <Navigation className="w-3 h-3 mr-1" />
                            Use My Location
                          </>
                        )}
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Street, Area, Landmark"
                          rows={2}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <div className="relative">
                            <select
                              name="state"
                              value={formData.state}
                              onChange={handleInputChange}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
                              required
                            >
                              <option value="">Select State</option>
                              {Object.keys(indianStatesAndCities).map(state => (
                                <option key={state} value={state}>{state}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                            <div className="relative">
                            <select
                              name="city"
                              value={formData.city}
                              onChange={handleInputChange}
                              disabled={!formData.state || cities.length === 0}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white disabled:bg-gray-100"
                              required
                            >
                              <option value="">Select District</option>
                              {cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="123456"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      "Register Business"
                    )}
                  </button>

                  <div className="text-center text-sm text-gray-600 mt-4">
                    Already registered?{" "}
                    <button
                      type="button"
                      onClick={() => setIsLogin(true)}
                      className="text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Sign In
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
    </>
  );
};

export default ContractorLogin;
